import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/signup.css";
import {
  CustomSwal,
  showOtpPrompt,
  maskEmail,
  maskMobile,
} from "../services/customSwal";
import {
  getStaffById,
  sendSignupOtp,
  verifySignupOtp,
  insertUser,
  checkLoginIdExists
} from "../api/signupApiService";
import FloatingSelect from "../Components/FloatingSelect";

// Lock helpers
const LOCK_DURATION = 30 * 60 * 1000; // 30 minutes

const isLocked = (staffId: string) => {
  const data = localStorage.getItem(`signup_lock_${staffId}`);
  if (!data) return false;
  const { lockedAt } = JSON.parse(data);
  const now = Date.now();
  if (now - lockedAt < LOCK_DURATION) return true;
  localStorage.removeItem(`signup_lock_${staffId}`);
  return false;
};

const setLock = (staffId: string) => {
  localStorage.setItem(
    `signup_lock_${staffId}`,
    JSON.stringify({ lockedAt: Date.now() })
  );
};


const Signup: React.FC = () => {
  const [form, setForm] = useState({
    staffId: "",
    staffName: "",
    loginId: "",
    instId: "",
    inst_id: "",
    department: "",
    designation: "",
    email: "",
    mobileNo: "",
    password: "",
    repassword: "",
  });

  const [actualEmail, setActualEmail] = useState("");
  const [actualMobile, setActualMobile] = useState("");
  //const [instId, setInstId] = useState("");

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  //const [institutions, setInstitutions] = useState<InstitutionData[]>([]);
  const [staffData, setStaffData] = useState<any[]>([]);
  const [isInstitutionSelectable, setIsInstitutionSelectable] = useState(false);
  const [isLoginExists, setIsLoginExists] = useState(false);
  // OTP caching
  const [otpCache, setOtpCache] = useState<{
    type: string;
    value: string;
    timestamp: number;
  } | null>(null);

  // Store verified OTPs
  const [verifiedEmailOtp, setVerifiedEmailOtp] = useState("");
  const [verifiedMobileOtp, setVerifiedMobileOtp] = useState("");

  const loginIdRef = useRef<HTMLInputElement>(null);
  const institutionRef = useRef<HTMLSelectElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const repasswordRef = useRef<HTMLInputElement>(null);
  const skipStaffBlurRef = useRef(false);
  const staffDetailsFetchedRef = useRef(false);
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: false });
  };

  const fetchStaffDetails = async () => {
    try {
      const staffId = form.staffId.trim();

      setIsLoginExists(false);

      if (!staffId) return;

      if (isLocked(staffId)) {
        await CustomSwal.fire(
          "Blocked",
          "Too many failed attempts. Please try again after some time.",
          "error"
        );
        return;
      }

      const data = await getStaffById(staffId);

      if (data.status === "1" && data.Data?.length) {
        const staffList = data.Data;

        setStaffData(staffList);

        // =========================================
        // ONLY ONE INSTITUTION
        // =========================================
        debugger;
        if (staffList.length === 1) {
          setIsInstitutionSelectable(false);

          const staff = staffList[0];

          if (
            staff.login_id !== null &&
            staff.login_id !== ""
          ) {
            setIsLoginExists(true);

            await CustomSwal.fire(
              "Login Already Exists",
              `Login id is ${staff.login_id}`,
              "warning"
            );

            setForm((prev) => ({
              ...prev,
              staffName: "",
              department: "",
              designation: "",
              email: "",
              mobileNo: "",
            }));

            setActualEmail("");
            setActualMobile("");

            // Focus Login ID
            setTimeout(() => {
              loginIdRef.current?.focus();
            }, 100);

            return;
          }

          setForm((prev) => ({
            ...prev,
            staffName: staff.staff_name || "",
            instId: staff.inst_name || "",
            inst_id: staff.inst_id || "",
            department: staff.user_dept || "",
            designation: staff.user_desig || "",
            email: maskEmail(staff.email_id) || "",
            mobileNo: maskMobile(staff.mobile) || "",
          }));

          setActualEmail(staff.email_id || "");
          setActualMobile(staff.mobile || "");

          // Focus Login ID
          setTimeout(() => {
            loginIdRef.current?.focus();
          }, 100);

          return;
        }
        else {
          // =========================================
          // MULTIPLE INSTITUTIONS
          // =========================================

          setIsInstitutionSelectable(true);
          setIsLoginExists(false);

          setForm((prev) => ({
            ...prev,
            staffName: "",
            instId: "",
            inst_id: "",
            department: "",
            designation: "",
            email: "",
            mobileNo: "",
          }));

          setActualEmail("");
          setActualMobile("");

          // Focus Institution
          setTimeout(() => {
            institutionRef.current?.focus();
          }, 100);

          //document.getElementById("institution")?.focus();
          return;
        }
      }
      else {
        // =========================================
        // NO STAFF FOUND
        // =========================================
        setStaffData([]);
        setIsInstitutionSelectable(false);
        setIsLoginExists(false);

        setForm((prev) => ({
          ...prev,
          staffId: "",
          staffName: "",
          instId: "",
          inst_id: "",
          department: "",
          designation: "",
          email: "",
          mobileNo: "",
        }));

        setActualEmail("");
        setActualMobile("");

        await CustomSwal.fire(
          "Not Found",
          "Staff ID not recognized. Please contact administrator.",
          "warning"
        );

        // Keep focus on Staff ID
        document.getElementById("staffId")?.focus();
      }
    } catch (err) {
      console.error("Error fetching staff:", err);

      setIsLoginExists(false);

      await CustomSwal.fire(
        "Error",
        "Unable to fetch staff details",
        "error"
      );

      document.getElementById("staffId")?.focus();
    }
  };

  const handleInstitutionChange = (selectedInstId: string) => {
    if (!selectedInstId) {
      setForm((prev) => ({
        ...prev,
        staffName: "",
        instId: "",
        inst_id: "",
        department: "",
        designation: "",
        email: "",
        loginId: "",
        password: "",
        repassword: "",
        mobileNo: "",
      }));

      setActualEmail("");
      setActualMobile("");
      setTimeout(() => {
        loginIdRef.current?.focus();
      }, 100);
      return;
    }

    const selectedStaff = staffData.find(
      (item) => String(item.inst_id) === String(selectedInstId)
    );

    if (!selectedStaff) return;

    // Login already exists
    if (
      selectedStaff.login_id !== null &&
      selectedStaff.login_id !== ""
    ) {
      CustomSwal.fire(
        "Login Already Exists",
        `Login ID is ${selectedStaff.login_id} for ${selectedStaff.staff_name} (${selectedStaff.staff_id})`,
        "warning"
      ).then(() => {
        if (staffData.length > 1) {
          setTimeout(() => {
            institutionRef.current?.focus();
          }, 100);
        }
      });

      setForm((prev) => ({
        ...prev,
        staffName: "",
        instId: selectedStaff.inst_name || "",
        inst_id: selectedStaff.inst_id || "",
        department: "",
        designation: "",
        loginId: "",
        email: "",
        mobileNo: "",
      }));

      setActualEmail("");
      setActualMobile("");

      return;
    }



    // Assign selected institution
    setForm((prev) => ({
      ...prev,
      staffName: selectedStaff.staff_name || "",
      instId: selectedStaff.inst_name || "",
      inst_id: selectedStaff.inst_id || "",
      department: selectedStaff.user_dept || "",
      designation: selectedStaff.user_desig || "",
      loginId: selectedStaff.loginid || "",
      password: "",
      repassword: "",
      email: maskEmail(selectedStaff.email_id) || "",
      mobileNo: maskMobile(selectedStaff.mobile) || "",
    }));

    setActualEmail(selectedStaff.email_id || "");
    setActualMobile(selectedStaff.mobile || "");
  };

  const passwordsMatch = form.password === form.repassword;
  const passwordTooShort = form.password.length < 6;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);

    // Validation
    const newErrors: { [key: string]: boolean } = {};
    if (!form.staffId) newErrors.staffId = true;
    if (!form.staffName) newErrors.staffName = true;
    if (!form.instId) newErrors.instId = true;
    if (!form.loginId) newErrors.loginId = true;
    if (!form.department) newErrors.department = true;
    if (!form.designation) newErrors.designation = true;
    /*     if (!form.email) newErrors.email = true;
        if (!form.mobileNo) newErrors.mobileNo = true; */
    if (!form.email && !form.mobileNo) {
      newErrors.email = true;
      newErrors.mobileNo = true;
    }

    if (!form.password || passwordTooShort) newErrors.password = true;
    if (!form.repassword || !passwordsMatch) newErrors.repassword = true;

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const staffId = form.staffId.trim();

    // Check lock
    if (isLocked(staffId)) {
      await CustomSwal.fire(
        "Blocked",
        "Too many failed attempts. Please try again after some time.",
        "error"
      );
      return;
    }

    try {
      // -----------------------
      // Step 1: Confirm Identity
      // -----------------------
      // Step 1: Confirm Identity (max 3 attempts)
      let confirmAttempts = 0;
      let type: 'email' | 'mobile' | null = null;
      let entered: string = '';

      while (confirmAttempts < 3) {
        const { value: confirmValue } = await CustomSwal.fire({
          title: 'Confirm Identity',
          input: 'text',
          inputLabel: 'Enter your registered Email or Mobile',
          inputPlaceholder: 'Email / Mobile',
          showCancelButton: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        if (!confirmValue) {
          CustomSwal.fire('Cancelled', 'Confirmation is required.', 'warning');
          return;
        }

        entered = confirmValue.trim();

        if (/^\d+$/.test(entered)) {
          type = 'mobile';
          if (entered === actualMobile) break;
        } else {
          type = 'email';
          if (entered.toLowerCase() === actualEmail.toLowerCase()) break;
        }

        confirmAttempts++;
        if (confirmAttempts < 3) {
          await CustomSwal.fire(
            'Mismatch',
            `Does not match registered. Attempts left: ${3 - confirmAttempts}`,
            'error'
          );
        } else {
          await CustomSwal.fire(
            'Blocked',
            'Maximum attempts reached. Try again after some time.',
            'error'
          );
          setLock(staffId);
          return;
        }
      }


      // -----------------------
      // Step 2: OTP
      // -----------------------
      const now = Date.now();
      if (
        otpCache &&
        otpCache.type === type &&
        otpCache.value === entered &&
        now - otpCache.timestamp < 5 * 60 * 1000
      ) {
        await CustomSwal.fire(
          "Warning",
          "Please try again later.",
          "warning"
        );
        return;
      }

      const otpSendRes = await sendSignupOtp(type!, entered!);
      
      if (!otpSendRes || otpSendRes.result !== "1") {
        await CustomSwal.fire("Warning", "Try again after some time.", "warning");
        return;
      }
      setOtpCache({ type: type!, value: entered!, timestamp: now });

      let otpAttempts = 0;
      let otpVerified = false;
      let otpValue = "";

      while (otpAttempts < 3 && !otpVerified) {
        const otp = await showOtpPrompt(
          type === "mobile" ? entered! : undefined,
          type === "email" ? entered! : undefined
        );

        if (!otp) {
          await CustomSwal.fire(
            "Cancelled",
            "OTP verification is required.",
            "warning"
          );
          return;
        }

        const otpVerifyRes = await verifySignupOtp(type!, entered!, otp);
        

        if (otpVerifyRes && otpVerifyRes.result === '1') {
          otpVerified = true;
          otpValue = otp;
          break;
        } else {
          otpAttempts++;
          if (otpAttempts < 3) {
            await CustomSwal.fire(
              "Invalid OTP",
              `Incorrect OTP. Attempts left: ${3 - otpAttempts}`,
              "error"
            );
          } else {
            await CustomSwal.fire(
              "Blocked",
              "Maximum OTP attempts reached. Try again after some time.",
              "error"
            );
            setLock(staffId);
            return;
          }
        }
      }

      // ✅ Save OTPs
      let emailOtpValue = verifiedEmailOtp;
      let mobileOtpValue = verifiedMobileOtp;

      if (type === "email") emailOtpValue = otpValue;
      if (type === "mobile") mobileOtpValue = otpValue;

      // -----------------------
      // Step 3: Save User
      // -----------------------
      const payload = {
        staff_id: form.staffId,
        user_name: form.staffName,
        inst_id: form.inst_id,
        login_id: form.loginId,
        user_dept: form.department,
        user_desig: form.designation,
        email_id: actualEmail,
        mobile: actualMobile,
        Password: form.password,
        Emailotp: emailOtpValue || "",
        Mobileotp: mobileOtpValue || "",
        EVerify_tag: emailOtpValue ? "Y" : "N",
        MVerify_tag: mobileOtpValue ? "Y" : "N",
      };
      
      const result = await insertUser(payload);
      if (result?.status === "1") {
        CustomSwal.fire("Success", "Account created successfully", "success").then(() =>
          navigate("/")
        );
      } else {
        CustomSwal.fire("Warning", result?.message, "warning");
      }
    } catch (error) {
      CustomSwal.fire("Error", "Something went wrong during signup", "error");
    }
  };

  const handleClear = () => {
    setForm({
      staffId: "",
      staffName: "",
      instId: "",
      inst_id: "",
      loginId: "",
      department: "",
      designation: "",
      email: "",
      mobileNo: "",
      password: "",
      repassword: "",
    });
    setStaffData([]);
    setFormSubmitted(false);
    setErrors({});
    setActualEmail("");
    setActualMobile("");
    setOtpCache(null);
    setVerifiedEmailOtp("");
    setVerifiedMobileOtp("");
    setIsInstitutionSelectable(false);
    setIsLoginExists(false);
  };

  useEffect(() => {
    if (
      form.staffId.trim() !== "" &&
      staffData.length > 0 &&
      !form.inst_id
    ) {
      const firstInstitution = staffData[0];

      handleInstitutionChange(
        String(firstInstitution.inst_id)
      );
    }
  }, [staffData]);

  const validateLoginId = async (): Promise<boolean> => {
    const value = form.loginId.trim();

    if (!value) {
      return false;
    }

    try {
      const res: any = await checkLoginIdExists(value);

      if (res?.Status === "2") {
        const result = await CustomSwal.fire({
          title: "Login ID Already Exists",
          text: "Please give another one.",
          icon: "warning",
          confirmButtonText: "OK",
          focusConfirm: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        // User clicked OK
        if (result.isConfirmed) {
          setForm((prev) => ({
            ...prev,
            loginId: "",
          }));

          setTimeout(() => {
            loginIdRef.current?.focus();
          }, 100);
        }

        return false;
      }

      return true;
    } catch (err) {
      console.error("Login ID check error:", err);
      return false;
    }
  };

  return (
    <div className="full-background">
      <video autoPlay muted loop playsInline className="bg-video">
        <source src="/assets/BgVideo.mp4" type="video/mp4" />
      </video>

      <div className="signup-card">
        <div className="signup-header-bar">
          <h4 className="signup-title">Sign Up</h4>
        </div>

        <form onSubmit={handleSubmit} className="signupform" autoComplete="off">
          {formSubmitted && Object.keys(errors).length > 0 && (
            <span className="form-errors">
              <p className="error-msg">Please fill in required fields correctly.</p>
            </span>
          )}

          {/* Staff ID & Institution */}

          <div className="formGroup">

            {/* Staff ID */}
            <div className="floating-label-group">
              <i className="fas fa-solid fa-id-card input-icon"></i>

              <input
                type="text"
                id="staffId"
                placeholder=" "
                value={form.staffId}
                onChange={(e) => {
                  const value = e.target.value;

                  if (/^[a-zA-Z0-9_]*$/.test(value)) {
                    handleChange("staffId", value);
                    staffDetailsFetchedRef.current = false;
                  }
                }}
                onBlur={async () => {
                  // Don't fetch again if Enter/Tab already triggered fetch
                  if (skipStaffBlurRef.current) {
                    skipStaffBlurRef.current = false;
                    return;
                  }

                  if (form.staffId.trim() !== "") {
                    await fetchStaffDetails();
                  }
                }}
                onKeyDown={async (e) => {
                  // ENTER
                  if (e.key === "Enter") {
                    e.preventDefault();

                    if (form.staffId.trim() === "") {
                      return;
                    }

                    skipStaffBlurRef.current = true;

                    await fetchStaffDetails();

                    return;
                  }

                  // TAB
                  if (e.key === "Tab" && form.staffId.trim() !== "") {
                    e.preventDefault();

                    skipStaffBlurRef.current = true;

                    await fetchStaffDetails();

                    return;
                  }
                }}
              />

              <label
                htmlFor="staffId"
                className={errors.staffId ? "text-danger" : ""}
              >
                Staff ID
              </label>
            </div>


            {/* Institution */}
            <div className="floating-label-group institution-group">
              <i className="fas fa-solid fa-building input-icon"></i>

              <select
                ref={institutionRef}
                id="institution"
                value={form.inst_id || ""}
                disabled={!isInstitutionSelectable}
                onChange={(e) => {
                  handleInstitutionChange(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Tab" || e.key === "Enter") {
                    e.preventDefault();
                    loginIdRef.current?.focus();
                  }
                }}
                className={form.inst_id ? "has-value" : ""}

              >


                {staffData.map((item) => (
                  <option
                    key={String(item.inst_id)}
                    value={item.inst_id}
                  >
                    {item.inst_name}
                  </option>
                ))}
              </select>

              <label
                htmlFor="institution"
                className={form.inst_id ? "active" : ""}
              >
                Institution
              </label>
            </div>
          </div>
          {/* loginId & staffName */}
          <div className="formGroup">


            <div className="floating-label-group">
              <i className="fas fa-solid fa-user input-icon"></i>
              <input type="text" id="staffName" placeholder=" " value={form.staffName} disabled />
              <label htmlFor="staffName" className={errors.staffName ? 'text-danger' : ''}>
                Staff Name
              </label>
            </div>
            <div className="floating-label-group">
              <i className="fas fa-solid fa-building input-icon"></i>
              <input type="text" id="department" placeholder=" " value={form.department} disabled />
              <label htmlFor="department" className={errors.department ? 'text-danger' : ''}>
                Department
              </label>
            </div>
          </div>

          {/* department & designation */}
          <div className="formGroup">

            <div className="floating-label-group">
              <i className="fas fa-solid fa-briefcase input-icon"></i>
              <input type="text" id="designation" placeholder=" " value={form.designation} disabled />
              <label htmlFor="designation" className={errors.designation ? 'text-danger' : ''}>
                Designation
              </label>
            </div>

            <div className="floating-label-group">
              <i className="fas fa-solid fa-envelope input-icon"></i>
              <input type="email" id="email" placeholder=" " value={form.email} disabled />
              <label htmlFor="email" className={errors.email ? 'text-danger' : ''}>
                Email
              </label>
            </div>
          </div>

          {/* email & Mobile */}
          <div className="formGroup">

            <div className="floating-label-group">
              <i className="fas fa-solid fa-user-circle input-icon"></i>
              {/* Login ID Field */}
              <input
                ref={loginIdRef}
                type="text"
                id="loginId"
                placeholder=" "
                value={form.loginId}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[a-zA-Z0-9_]*$/.test(value)) handleChange("loginId", value);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Tab" || e.key === "Enter") {
                    e.preventDefault();

                    const isValid = await validateLoginId();

                    if (isValid) {
                      passwordRef.current?.focus();
                    }
                  }
                }}
                onBlur={async () => {
                  await validateLoginId();
                }}
               
                tabIndex={1}
              />
              <label htmlFor="loginId" className={errors.loginId ? 'text-danger' : ''}>
                Login Id
              </label>
            </div>

            <div className="floating-label-group">
              <i className="fas fa-solid fa-mobile-screen-button input-icon"></i>
              <input type="text" id="mobileNo" placeholder=" " value={form.mobileNo} disabled />
              <label htmlFor="mobileNo" className={errors.mobileNo ? 'text-danger' : ''}>
                Mobile No
              </label>
            </div>
          </div>

          {/* Password & Confirm Password */}
          <div className="formGroup">
            <div className="floating-label-group">
              <i className="fas fa-solid fa-lock input-icon"></i>
              <input
                ref={passwordRef}
                type={showPassword ? 'text' : 'password'}
                id="password"
                placeholder=" "
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value.replace(/\s/g, ''))}
                onKeyDown={(e) => {
                  if (e.key === "Tab" || e.key === "Enter") {
                    e.preventDefault();
                    repasswordRef.current?.focus();
                  }
                }}
              />
              <label htmlFor="password" className={errors.password ? 'text-danger' : ''}>
                New Password <span className="label-with-error">
                  {form.password && (
                    <>
                      {passwordTooShort && (
                        <span className="error-text">Password must be at least 6 characters.</span>
                      )}
                    </>
                  )}
                </span>
              </label>
              <i
                className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} toggle-password-icon`}
                onClick={() => setShowPassword(!showPassword)}
                title="Toggle Password"
              ></i>
            </div>
            <div className="floating-label-group">
              <i className="fas fa-solid fa-lock input-icon"></i>
              <input
                type={showPassword ? 'text' : 'password'}
                id="repassword"
                placeholder=" "
                value={form.repassword}
                onChange={(e) => handleChange('repassword', e.target.value.replace(/\s/g, ''))}
                ref={repasswordRef}
              />
              <label htmlFor="repassword" className={errors.repassword ? 'text-danger' : ''}>
                Confirm Password <span className="label-with-error">
                  {form.repassword && (
                    <>
                      {!passwordsMatch && !passwordTooShort && (
                        <span className="error-text">Passwords do not match.</span>
                      )}
                    </>
                  )}
                </span>
              </label>
            </div>
          </div>
          <div className="form-button">
            <button
              type="submit"
              className="form-btn signup-btn"
              disabled={isLoginExists}
            >
              Create
            </button>
            {/* <button type="submit" className="form-btn signup-btn">
              Create
            </button> */}

            <button type="button" className="form-btn clear-btn" onClick={handleClear}>
              Clear
            </button>
          </div>

          <div className="login-link">
            Already have an account? <Link to="/">Login</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;
